<script setup lang="ts">
import type { ContentChatbot } from '~/models/Content/ContentChatbot'
import { useI18n } from 'vue-i18n'
import { ref, useTemplateRef, watch } from 'vue'
import { useChatbotSubmit } from '~/composables/useChatbotSubmit'
import ChatbotSubmitButton from '~/components/chat/ChatbotSubmitButton.vue'

const props = defineProps<{
  bot?: ContentChatbot
  prompt: (value: string, initial?: boolean) => Promise<void>
  isLoading: boolean
  isStreaming: boolean
  isInteracting: boolean
}>()

const { t } = useI18n()
const inputValue = ref('')
const chatInput = useTemplateRef<HTMLInputElement>('chatInput')

const { submit, interactions } = useChatbotSubmit(props.bot, props.prompt)

const handleSubmit = () => {
  if (!inputValue.value) {
    return
  }
  submit(inputValue.value)
  inputValue.value = ''
}

watch(interactions, (actions) => {
  if (!chatInput.value || !actions)
    return
  chatInput.value.focus()
})

// Expose focus method so parent component can programmatically focus the input
defineExpose({
  focus: () => chatInput.value?.focus(),
})
</script>

<template>
  <div class="relative bottom-0 flex w-full">
    <input
      ref="chatInput"
      v-model="inputValue"
      class="placeholder:text-gray-30 w-full rounded-xl bg-white py-3 pr-14 pl-4 text-(--theme-60,black) ring-(--theme-50,black) transition focus:ring disabled:cursor-not-allowed disabled:opacity-95"
      type="text"
      :placeholder="t('chatbot.placeholder')"
      :disabled="isLoading || isStreaming || isInteracting"
      @keydown.enter="handleSubmit"
    >
    <ChatbotSubmitButton
      class="absolute top-1/2 right-1 -translate-y-1/2"
      :is-loading="isLoading"
      :is-streaming="isStreaming"
      :is-interacting="isInteracting"
      :disabled="!inputValue.length"
      @submit="handleSubmit"
    />
  </div>
</template>
